import React, { useState, useEffect } from 'react';
import {
  getProject,
  getProjectSummary,
  updateProject,
  uploadProjectLogo,
  getPMList,
  getProgressAlertsSummary,
  getDependencyAlerts,
} from '../api';
import LoadingState from '../components/LoadingState';
import SOVTab from '../components/SOVTab';
import TasksTab from '../components/TasksTab';
import GanttTab from '../components/GanttTab';
import ScheduleView from '../components/ScheduleView';
import BillingTab from '../components/BillingTab';
import ForecastTab from '../components/ForecastTab';
import ChangeOrdersTab from '../components/ChangeOrdersTab';
import ContractValueTab from '../components/ContractValueTab';
import ClientViewTab from '../components/ClientViewTab';
import RollupTab from '../components/RollupTab';
import SchedulePerformanceTab from '../components/SchedulePerformanceTab';
import JobCostTab from '../components/JobCostTab';
import { useTheme } from '../hooks/useTheme';
import ProjectHeader from '../components/project/ProjectHeader';
import TabBar from '../components/project/TabBar';
import KpiStrip from '../components/project/KpiStrip';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  Card,
  Field,
  InlineEdit,
  PMPills,
  Toggle,
  StatusBadge,
  AlertRow,
  LogoUpload,
  SaveBar,
} from '../components/project/overview';

const TABS = [
  { key: 'dashboard', label: 'Overview' },
  { key: 'sov', label: 'SOV' },
  { key: 'tasks', label: 'Schedule' },
  { key: 'rollup', label: 'Rollup' },
  { key: 'billing', label: 'Billing' },
  { key: 'performance', label: 'Performance' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'contract value', label: 'Contract Value' },
  { key: 'job-costs', label: 'Job Costs' },
  { key: 'closeout', label: 'Closeout' },
  { key: 'client view', label: 'Client View' },
];

/* ── Project Information card helpers (regrouped 4-section layout) ── */

// Derived project duration, shown on the Timeline section's label row.
const durationLabel = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (!(ms > 0)) return null;
  const months = ms / (1000 * 60 * 60 * 24 * 30.44);
  return months < 1.5
    ? `${Math.round(ms / (1000 * 60 * 60 * 24))} days`
    : `${Math.round(months)} months`;
};

// Full-bleed, hairline-divided card section with an optional uppercase
// micro-label + right-aligned `side` slot. First section omits the divider
// (the card header already draws one).
const PISection = ({ label, side, first = false, children }) => (
  <div style={{
    borderTop: first ? 'none' : '1px solid var(--border-subtle)',
    padding: first ? '16px 16px 15px' : '13px 16px 15px',
  }}>
    {label && (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
        <div style={{ flex: 1 }} />
        {side}
      </div>
    )}
    {children}
  </div>
);

// De-emphasized read-only footer strip for Contract value (its headline
// already leads the KPI strip above) — sits one step below the surface.
const PISyncedRow = ({ label, value, hint }) => (
  <div title={hint} style={{
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '9px 16px', background: 'var(--bg-surface-2)',
    borderTop: '1px solid var(--border-subtle)',
  }}>
    <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)', letterSpacing: '0.02em' }}>{label}</span>
    <div style={{ flex: 1 }} />
    <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 500, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>SOV · synced</span>
    <span style={{ color: 'var(--text-4)', display: 'inline-flex' }} title="Read-only">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
    </span>
  </div>
);

// Calendar prefix for the date fields.
const CalIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>
);

const ProjectDetail = ({ projectId, onBack, activeTab: externalTab, setActiveTab: externalSetActiveTab, user, darkMode, onToggleDarkMode, onLogout }) => {
  const [internalTab, setInternalTab] = useState('dashboard');
  const activeTab = externalTab || internalTab;
  const setActiveTab = externalSetActiveTab || setInternalTab;
  // Performance sub-view (Schedule / Billing) state lives inside the
  // redesigned SchedulePerformanceTab component now — no longer needs
  // to be tracked here.
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  // ScheduleView now owns its own task-edit modal; we just keep a refresh key
  // for callers (if any) that want to force a re-fetch by bumping it.
  const [ganttRefreshKey] = useState(0);
  const [pmList, setPMList] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [editingCollected, setEditingCollected] = useState(false);
  const [collectedInput, setCollectedInput] = useState('');
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [depAlerts, setDepAlerts] = useState(null);
  const t = useTheme();

  useEffect(() => {
    loadProjectData();
    getPMList().then(setPMList).catch(console.error);
    getProgressAlertsSummary(projectId).then(setAlertsSummary).catch(console.error);
    getDependencyAlerts(projectId).then(setDepAlerts).catch(console.error);
  }, [projectId]);

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const [projectData, summaryData] = await Promise.all([
        getProject(projectId),
        getProjectSummary(projectId),
      ]);
      setProject(projectData);
      setSummary(summaryData);
      setEditedProject(projectData);
    } catch (error) {
      console.error('Error loading project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSummary = async () => {
    try {
      const summaryData = await getProjectSummary(projectId);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error refreshing summary:', error);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      await updateProject(projectId, editedProject);
      setProject(editedProject);
      await refreshSummary();
    } catch (error) {
      console.error('Error saving project:', error);
      if (error.code === 'DUPLICATE_PROJECT_NUMBER') {
        alert(error.message);
      } else {
        alert('Failed to save project. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedProject({ ...editedProject, [field]: value });
  };

  const handleCollectedSave = async () => {
    const val = parseFloat(collectedInput);
    if (isNaN(val) || val < 0) return;
    try {
      await updateProject(projectId, { collected_to_date: val });
      await loadProjectData();
      setEditingCollected(false);
    } catch (err) { console.error('Failed to update collected:', err); }
  };

  const handleCurrencyEditChange = (field, value) => {
    const raw = value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    setEditedProject({ ...editedProject, [field]: cleaned });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB.');
      return;
    }
    setLogoUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Resize image to max 200x200 for storage efficiency
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const max = 200;
          let w = img.width, h = img.height;
          if (w > max || h > max) {
            if (w > h) { h = Math.round(h * max / w); w = max; }
            else { w = Math.round(w * max / h); h = max; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/png');
          await uploadProjectLogo(projectId, dataUrl);
          setProject(prev => ({ ...prev, logo_url: dataUrl }));
          setEditedProject(prev => ({ ...prev, logo_url: dataUrl }));
          setLogoUploading(false);
        };
        img.src = reader.result;
      } catch (err) {
        console.error('Logo upload failed:', err);
        alert('Failed to upload logo.');
        setLogoUploading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveLogo = async () => {
    try {
      setLogoUploading(true);
      await uploadProjectLogo(projectId, null);
      setProject(prev => ({ ...prev, logo_url: null }));
      setEditedProject(prev => ({ ...prev, logo_url: null }));
    } catch (err) {
      console.error('Failed to remove logo:', err);
    } finally {
      setLogoUploading(false);
    }
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    const parts = String(value).split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  if (loading) {
    return <LoadingState message="Loading project data…" />;
  }

  if (!project || !summary) {
    return (
      <div style={{ padding: '20px' }}>
        <button className="btn btn-secondary" onClick={onBack}>
          &larr; Back to Projects
        </button>
        <p style={{ color: '#f44336', marginTop: '16px' }}>Error loading project data</p>
      </div>
    );
  }

  const contractVal = summary.contract_value || 0;
  const allocatedVal = summary.allocated_value || 0;
  const billedVal = summary.billed_to_date || 0;
  const retainageVal = summary.retainage_held || 0;
  const remainingVal = summary.remaining || 0;
  const billedPercent = contractVal > 0 ? ((billedVal / contractVal) * 100).toFixed(1) : 0;

  // Days remaining in project
  const projectEndDate = summary.latest_task_finish || project.end_date;
  const daysRemaining = projectEndDate
    ? Math.max(0, Math.ceil((new Date(projectEndDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Redesigned chrome — sits inside .app-main's 24px gutter so
          the outer corners on ProjectHeader (top) and KpiStrip
          (bottom) can show their 12px rounding against the
          --bg-page background. Top padding on .app-main was zeroed
          for project-detail routes (so sticky positioning anchors at
          y=0 without offsetting the TabBar), but a small top margin
          here gives the rounded top a comfortable breathing point. */}
      <div style={{ margin: '12px 0 0' }}>
        <ProjectHeader
          project={project}
          summary={summary}
          user={user}
          onBack={onBack}
          formatDate={formatDate}
          darkMode={darkMode}
          onToggleDarkMode={onToggleDarkMode}
          onLogout={onLogout}
        />
        <TabBar
          tabs={TABS.filter((t) => t.key !== 'client view')}
          activeTab={activeTab}
          onChange={setActiveTab}
          showClientView={TABS.some((t) => t.key === 'client view')}
        />
        {/* 8px gutter of --bg-page so the TabBar and KpiStrip read as
            two distinct bands instead of one fused chrome region.
            Inner wrapper also adds the strip's top border so the two
            bands hand off cleanly. */}
        <div style={{ background: 'var(--bg-page)', paddingTop: 8 }}>
          <KpiStrip
            contractValue={contractVal}
            sovAllocated={allocatedVal}
            billedToDate={billedVal}
            remaining={remainingVal}
            daysRemaining={daysRemaining}
            collectedToDate={parseFloat(project?.collected_to_date) || 0}
            outstanding={Math.max(0, billedVal - (parseFloat(project?.collected_to_date) || 0))}
            retainageHeld={retainageVal}
            billedPercent={parseFloat(billedPercent) || 0}
            onEditCollected={() => {
              if (!editingCollected) {
                setCollectedInput((parseFloat(project?.collected_to_date) || 0).toFixed(2));
                setEditingCollected(true);
              }
            }}
          />
        </div>
      </div>

      {/* Edit Collected popover — small floating dialog above the page so the
          tight KPI cell doesn't need to host an inline input. */}
      {editingCollected && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setEditingCollected(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              borderRadius: 10, padding: 16, minWidth: 280, boxShadow: 'var(--shadow-md)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>Update Collected to Date</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16.5, color: 'var(--text-2)' }}>$</span>
              <input
                type="number" step="0.01" min="0"
                value={collectedInput}
                onChange={(e) => setCollectedInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCollectedSave(); if (e.key === 'Escape') setEditingCollected(false); }}
                autoFocus
                style={{
                  flex: 1, padding: '6px 8px', fontSize: 15.5,
                  border: '1px solid var(--border-default)', borderRadius: 6,
                  background: 'var(--bg-surface)', color: 'var(--text-1)', outline: 'none',
                  fontFamily: 'var(--font-mono)',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setEditingCollected(false)}
                style={{
                  padding: '6px 12px', fontSize: 14, fontWeight: 500, borderRadius: 6,
                  border: '1px solid var(--border-default)', background: 'var(--bg-surface)',
                  color: 'var(--text-2)', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={handleCollectedSave}
                style={{
                  padding: '6px 12px', fontSize: 14, fontWeight: 500, borderRadius: 6,
                  border: 'none', background: 'var(--accent)', color: 'white', cursor: 'pointer',
                }}
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content — wrapped in an in-chrome ErrorBoundary so a
          render failure inside one tab shows a polite state screen
          without nuking the ProjectHeader + TabBar + KpiStrip above. */}
      <ErrorBoundary variant="in-chrome">
      <div style={{ marginTop: '20px', maxWidth: '100%', overflowX: 'hidden' }}>

        {activeTab === 'dashboard' && (() => {
          // ------- Overview redesign body -------
          // 4 panels in a 2-col grid + SaveBar. Inline-edit on every
          // text field. Pending count = fields where editedProject diverges
          // from project; used to gate Save/Reset. Health panel only when
          // alerts exist; settings panel has Toggle + locked StatusBadge.
          const isDirty = (k) =>
            editedProject && project && editedProject[k] !== project[k];
          const pendingCount = editedProject && project
            ? Object.keys(editedProject).filter((k) => editedProject[k] !== project[k]).length
            : 0;
          const progressItems = alertsSummary?.summaries || [];
          const schedItems = (depAlerts?.alerts || []).slice(0, 5);
          const hasHealth = progressItems.length > 0 || schedItems.length > 0;
          const healthCount =
            progressItems.reduce((a, s) => a + (s.alert_count || 0), 0) +
            schedItems.length;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
                gap: 16, alignItems: 'flex-start',
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
                  <Card title="Project Information" padded={false} style={{ overflow: 'hidden' }}>
                    <div className="pi-body">
                      <style>{`
                        .pi-body { container-type: inline-size; }
                        .pi-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 18px; }
                        .pi-grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px 18px; }
                        .pi-identity { display: flex; gap: 16px; align-items: flex-start; }
                        @container (max-width: 520px) {
                          .pi-grid, .pi-grid-3 { grid-template-columns: 1fr; }
                          .pi-identity { flex-direction: column; }
                        }
                      `}</style>

                      {/* 1 · Identity — logo + name (big) + number / client */}
                      <PISection first>
                        <div className="pi-identity">
                          <LogoUpload
                            size={64}
                            src={editedProject?.logo_url || null}
                            uploading={logoUploading}
                            onUpload={(file) => handleLogoUpload({ target: { files: [file] } })}
                            onRemove={handleRemoveLogo}
                          />
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
                            <Field label="Project name">
                              <InlineEdit
                                big
                                value={editedProject?.project_name || ''}
                                placeholder="Untitled project"
                                pending={isDirty('project_name')}
                                ariaLabel="Edit project name"
                                onChange={(v) => handleEditChange('project_name', v)}
                              />
                            </Field>
                            <div className="pi-grid">
                              <Field label="Project number">
                                <InlineEdit
                                  mono
                                  value={editedProject?.project_number || ''}
                                  placeholder="e.g. WOODS-L-01"
                                  pending={isDirty('project_number')}
                                  ariaLabel="Edit project number"
                                  onChange={(v) => handleEditChange('project_number', v)}
                                />
                              </Field>
                              <Field label="Client">
                                <InlineEdit
                                  value={editedProject?.client_name || ''}
                                  placeholder="Client name"
                                  pending={isDirty('client_name')}
                                  ariaLabel="Edit client name"
                                  onChange={(v) => handleEditChange('client_name', v)}
                                />
                              </Field>
                            </div>
                          </div>
                        </div>
                      </PISection>

                      {/* 2 · Team — PM pills full-width; the two single-person fields pair below */}
                      <PISection label="Team">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                          <Field label={editedProject?.project_manager && editedProject.project_manager.includes(',') ? 'Project managers' : 'Project manager'}>
                            <PMPills
                              value={editedProject?.project_manager || ''}
                              pmList={pmList}
                              pending={isDirty('project_manager')}
                              onChange={(v) => handleEditChange('project_manager', v)}
                            />
                          </Field>
                          <div className="pi-grid-3">
                            <Field label="Construction manager">
                              <PMPills
                                single
                                addLabel="+ Set construction manager"
                                value={editedProject?.construction_manager || ''}
                                pmList={pmList}
                                pending={isDirty('construction_manager')}
                                onChange={(v) => handleEditChange('construction_manager', v)}
                              />
                            </Field>
                            <Field label="Salesperson">
                              <PMPills
                                single
                                addLabel="+ Set salesperson"
                                value={editedProject?.salesperson || ''}
                                pmList={pmList}
                                pending={isDirty('salesperson')}
                                onChange={(v) => handleEditChange('salesperson', v)}
                              />
                            </Field>
                            <Field label="Project developer">
                              <PMPills
                                single
                                addLabel="+ Set project developer"
                                value={editedProject?.project_developer || ''}
                                pmList={pmList}
                                pending={isDirty('project_developer')}
                                onChange={(v) => handleEditChange('project_developer', v)}
                              />
                            </Field>
                          </div>
                        </div>
                      </PISection>

                      {/* 3 · Timeline — dates + derived duration on the label row */}
                      <PISection
                        label="Timeline"
                        side={durationLabel(editedProject?.start_date, editedProject?.end_date) && (
                          <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>
                            {durationLabel(editedProject?.start_date, editedProject?.end_date)}
                          </span>
                        )}
                      >
                        <div className="pi-grid">
                          <Field label="Start date">
                            <InlineEdit
                              type="date" mono
                              value={editedProject?.start_date || ''}
                              placeholder="Pick a start date"
                              prefix={<CalIcon />}
                              pending={isDirty('start_date')}
                              ariaLabel="Edit start date"
                              onChange={(v) => handleEditChange('start_date', v)}
                            />
                          </Field>
                          <Field label="End date">
                            <InlineEdit
                              type="date" mono
                              value={editedProject?.end_date || ''}
                              placeholder="Pick an end date"
                              prefix={<CalIcon />}
                              pending={isDirty('end_date')}
                              ariaLabel="Edit end date"
                              onChange={(v) => handleEditChange('end_date', v)}
                            />
                          </Field>
                        </div>
                      </PISection>

                      {/* 4 · Contract value — demoted read-only footer (headline lives in the KPI strip) */}
                      <PISyncedRow
                        label="Contract value"
                        value={formatCurrency(
                          editedProject?.revised_contract_value ||
                          editedProject?.original_contract_value ||
                          editedProject?.contract_amount || 0
                        )}
                        hint="Synced from the Schedule of Values — managed in the Contract Value tab"
                      />
                    </div>
                  </Card>
                  <Card title="Project Settings">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '4px 0' }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--text-3)', flexShrink: 0,
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14V8M5 10v6"/><circle cx="12" cy="12" r="3"/><path d="M2 12h7M15 12h7"/></svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>Manual billing override</span>
                            <span style={{
                              fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em',
                              textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4,
                              background: 'var(--bg-surface-2)', color: 'var(--text-3)',
                              border: '1px solid var(--border-subtle)',
                            }}>{editedProject?.manual_billing_override ? 'Enabled' : 'Off'}</span>
                            {isDirty('manual_billing_override') && (
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />
                            )}
                          </div>
                          <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5, maxWidth: 540 }}>
                            When enabled, percent complete can be entered manually on billing period lines instead of being driven by the schedule. Use for cutover projects.
                          </div>
                        </div>
                        <Toggle
                          on={!!editedProject?.manual_billing_override}
                          onClick={() => handleEditChange('manual_billing_override', editedProject?.manual_billing_override ? 0 : 1)}
                        />
                      </div>
                      <div style={{ height: 1, background: 'var(--border-subtle)', margin: '14px 0' }} />
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '4px 0' }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: editedProject?.ce_integration_enabled ? 'var(--success-soft)' : 'var(--warning-soft)',
                          border: `1px solid color-mix(in oklch, ${editedProject?.ce_integration_enabled ? 'var(--success)' : 'var(--warning)'} 22%, transparent)`,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          color: editedProject?.ce_integration_enabled ? 'var(--success)' : 'var(--warning)',
                          flexShrink: 0,
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h16M4 17h10"/></svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>ComputerEase integration</span>
                            <StatusBadge
                              kind={editedProject?.ce_integration_enabled ? 'enabled' : 'disabled'}
                              label={editedProject?.ce_integration_enabled ? 'Enabled' : 'Disabled'}
                            />
                          </div>
                          <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5, maxWidth: 580 }}>
                            {editedProject?.ce_integration_enabled
                              ? 'Billing cycles for this project sync to ComputerEase (CREATE_SOV on first billing, UPDATE_SOV thereafter). Set at project creation — cannot be changed here.'
                              : 'Billing cycles for this project will not sync to ComputerEase. Set at project creation — cannot be changed here.'}
                          </div>
                        </div>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', borderRadius: 999,
                          background: 'var(--bg-surface-2)',
                          border: '1px solid var(--border-subtle)',
                          fontSize: 11.5, fontWeight: 600, letterSpacing: '0.04em',
                          color: 'var(--text-4)', textTransform: 'uppercase',
                          flexShrink: 0,
                        }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                          Locked
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
                  <Card title="Description & Scope">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <Field label="Description">
                        <InlineEdit
                          multiline value={editedProject?.description || ''}
                          placeholder="Add a short description of this project…"
                          pending={isDirty('description')}
                          ariaLabel="Edit description"
                          onChange={(v) => handleEditChange('description', v)}
                        />
                      </Field>
                      <Field label="Contract for" hint="Project scope description used on AIA pay applications in ComputerEase.">
                        <InlineEdit
                          value={editedProject?.contract_for || ''}
                          placeholder="e.g., General construction of new office building"
                          pending={isDirty('contract_for')}
                          ariaLabel="Edit contract scope"
                          onChange={(v) => handleEditChange('contract_for', v)}
                        />
                      </Field>
                    </div>
                  </Card>
                  {hasHealth && (
                    <Card
                      title="Project Health"
                      hint={`${healthCount} item${healthCount === 1 ? '' : 's'} need attention`}
                      padded={false}
                    >
                      {progressItems.length > 0 && (
                        <div style={{ padding: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-1)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V4h13l-2 4 2 4H4"/></svg>
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Tasks need progress updates</span>
                            <span className="mono" style={{
                              marginLeft: 'auto', fontSize: 12.5, fontWeight: 500,
                              color: 'var(--text-2)', background: 'var(--bg-surface-2)',
                              border: '1px solid var(--border-subtle)',
                              padding: '2px 8px', borderRadius: 999,
                            }}>
                              {progressItems.reduce((a, s) => a + (s.alert_count || 0), 0)} tasks
                              {progressItems.reduce((a, s) => a + (s.critical_count || 0), 0) > 0
                                ? ` · ${progressItems.reduce((a, s) => a + (s.critical_count || 0), 0)} critical`
                                : ''}
                            </span>
                          </div>
                          <div style={{
                            background: 'var(--warning-soft)',
                            border: '1px solid color-mix(in oklch, var(--warning) 18%, transparent)',
                            borderRadius: 8, overflow: 'hidden',
                          }}>
                            {progressItems.map((s, i) => (
                              <div key={i} style={{
                                borderTop: i === 0 ? 'none' : '1px solid color-mix(in oklch, var(--warning) 12%, transparent)',
                              }}>
                                <AlertRow
                                  severity={s.critical_count > 0 ? 'critical' : 'warning'}
                                  primary={s.period_name}
                                  secondary={s.days_until_end <= 0 ? 'Period ended' : `${s.days_until_end} day${s.days_until_end !== 1 ? 's' : ''} left`}
                                  count={`${s.alert_count} task${s.alert_count !== 1 ? 's' : ''}${s.critical_count > 0 ? ` · ${s.critical_count} critical` : ''}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {schedItems.length > 0 && (
                        <div style={{ padding: progressItems.length > 0 ? '0 16px 16px' : 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-1)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></svg>
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Scheduling alerts</span>
                            <span className="mono" style={{
                              marginLeft: 'auto', fontSize: 12.5, fontWeight: 500,
                              color: 'var(--text-2)', background: 'var(--bg-surface-2)',
                              border: '1px solid var(--border-subtle)',
                              padding: '2px 8px', borderRadius: 999,
                            }}>
                              top {schedItems.length}
                              {schedItems.filter(a => a.severity === 'critical').length > 0
                                ? ` · ${schedItems.filter(a => a.severity === 'critical').length} critical`
                                : ''}
                            </span>
                          </div>
                          <div style={{
                            background: 'color-mix(in oklch, var(--danger-soft) 50%, var(--bg-surface-2))',
                            border: '1px solid color-mix(in oklch, var(--danger) 14%, transparent)',
                            borderRadius: 8, overflow: 'hidden',
                          }}>
                            {schedItems.map((a, i) => (
                              <div key={i} style={{
                                borderTop: i === 0 ? 'none' : '1px solid color-mix(in oklch, var(--danger) 10%, transparent)',
                              }}>
                                <AlertRow
                                  severity={a.severity === 'critical' ? 'critical' : 'warning'}
                                  primary={a.message || a.type}
                                  secondary={a.context || (a.type === 'circular' ? 'Circular dependency' : a.type === 'date_violation' ? 'Date violation' : 'Missing dates')}
                                />
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop: 8, textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => setActiveTab('tasks')}
                              style={{
                                background: 'transparent', border: 'none',
                                color: 'var(--accent-text)', fontSize: 13, fontWeight: 500,
                                cursor: 'pointer', padding: 0,
                              }}
                            >View all in Schedule →</button>
                          </div>
                        </div>
                      )}
                    </Card>
                  )}
                </div>
              </div>
              <SaveBar
                pendingCount={pendingCount}
                saving={saving}
                onReset={() => setEditedProject(project)}
                onSave={handleSaveProject}
              />
            </div>
          );
        })()}

        {activeTab === 'sov' && (
          <SOVTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {(activeTab === 'tasks' || activeTab === 'gantt') && (
          <ScheduleView
            projectId={projectId}
            refreshSummary={refreshSummary}
            refreshKey={ganttRefreshKey}
          />
        )}
        {activeTab === 'rollup' && (
          <RollupTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'billing' && (
          <BillingTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'performance' && (
          <SchedulePerformanceTab projectId={projectId} />
        )}
        {activeTab === 'forecast' && (
          <ForecastTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'contract value' && (
          <ContractValueTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'job-costs' && (
          <JobCostTab projectId={projectId} project={project} />
        )}
        {activeTab === 'closeout' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 24px', gap: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>Closeout</h2>
              <span style={{ display: 'inline-block', marginTop: 8, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', padding: '3px 10px', borderRadius: 999 }}>Coming soon</span>
            </div>
            <p style={{ margin: 0, maxWidth: 420, fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
              Project closeout tools are on the way — we'll be building this out later this week.
            </p>
          </div>
        )}
        {activeTab === 'client view' && (
          <ClientViewTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
      </div>
      </ErrorBoundary>

    </div>
  );
};

export default ProjectDetail;
